import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { allowanceLabel, formatMinor, type BillingCurrency } from "@talenthop/shared";
import { ShieldCheck, Check, Info } from "../components/icons.js";
import { api, ApiError } from "../lib/api.js";
import { AuthBrand } from "./Login.js";

interface VerifyResponse {
  plan: "free" | "paid";
  requestId: string;
  status: string;
  /** legacy credit-pack signups (pre-0097) */
  credits?: number;
  currency?: string;
  amountMinor?: number;
  /** plan-era paid signups (0097) — the plan quoted exactly as the in-app checkout would */
  planName?: string;
  quantity?: number;
  allowances?: { jobs: number; resumes: number; interviews: number };
  taxMinor?: number;
  taxPct?: number;
}

// ---- Razorpay Checkout (loaded on demand — same idiom as users/Credits.tsx) ----
interface RzpResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}
interface RzpCtor {
  new (o: {
    key: string;
    order_id: string;
    amount: number;
    currency: string;
    name?: string;
    description?: string;
    handler: (r: RzpResponse) => void;
    modal?: { ondismiss?: () => void };
    theme?: { color?: string };
  }): { open: () => void };
}
declare global {
  interface Window {
    Razorpay?: RzpCtor;
  }
}
function loadRazorpay(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/** Consumes the /verify-signup?token=… link, then — for the paid path — hosts the guest
 *  checkout for the chosen plan (or a legacy credit pack). No session exists yet; every call here is scoped by
 *  the signup request's own unguessable id, not a JWT. */
export function VerifySignup(): ReactNode {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);

  const verify = useMutation({
    mutationFn: () => api.post<VerifyResponse>("/auth/verify-signup", { token }),
  });

  // Once per token: React StrictMode runs effects twice in dev, and a second call used to land on
  // an already-consumed token and replace the success page with "invalid or has expired".
  const verifiedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!token || verifiedFor.current === token) return;
    verifiedFor.current = token;
    verify.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function pay(): Promise<void> {
    if (!verify.data) return;
    setPayError(null);
    setPayBusy(true);
    try {
      const start = await api.post<{ purchaseId: string; orderId: string; keyId: string; currency: string; amountMinor: number }>(
        `/signup-requests/${verify.data.requestId}/purchase`,
      );
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        setPayError("Couldn't load the payment window. Check your connection and try again.");
        setPayBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: start.keyId,
        order_id: start.orderId,
        amount: start.amountMinor,
        currency: start.currency,
        name: "TalentHop",
        description: verify.data.planName
          ? `${verify.data.planName}${(verify.data.quantity ?? 1) > 1 ? ` × ${verify.data.quantity}` : ""}`
          : `${verify.data.credits ?? ""} credits`,
        theme: { color: "#0B2419" }, // Theme 1 forest — Razorpay needs a literal colour
        modal: { ondismiss: () => setPayBusy(false) },
        handler: (r) => {
          void api
            .post(`/signup-requests/${verify.data!.requestId}/purchase/confirm`, {
              razorpayOrderId: r.razorpay_order_id,
              razorpayPaymentId: r.razorpay_payment_id,
              razorpaySignature: r.razorpay_signature,
            })
            .then(() => {
              setPayBusy(false);
              setPaid(true);
            })
            .catch(() => {
              setPayBusy(false);
              setPayError("The payment went through but confirming it failed — refresh and check /login, or contact support.");
            });
        },
      });
      rzp.open();
    } catch (e) {
      setPayBusy(false);
      setPayError(e instanceof ApiError ? e.message : "Couldn't start the payment. Please try again.");
    }
  }

  if (!token) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>Verify your email</h1>
          </div>
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>This link is invalid or has expired. Please use the most recent email we sent you.</span>
          </div>
          <p className="auth-hint">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (verify.isPending || verify.isIdle) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>Verifying your email…</h1>
          </div>
        </div>
      </div>
    );
  }

  if (verify.isError) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>Verify your email</h1>
          </div>
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>This link is invalid or has expired. Please use the most recent email we sent you.</span>
          </div>
          <p className="auth-hint">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  const data = verify.data!;

  // Already approved — nothing left to do but sign in.
  if (data.status === "approved") {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <h1>Your account is ready</h1>
            <p>Your email is verified and your workspace is active.</p>
          </div>
          <Link to="/login" className="btn primary" style={{ width: "100%", justifyContent: "center" }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  // Free plan — verified, now waiting on a human. Nothing more to do here.
  if (data.plan === "free") {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>Email verified</h1>
          </div>
          <div className="banner info" style={{ marginBottom: 16 }}>
            <Check />
            <span>Thanks — your email is verified.</span>
          </div>
          <p className="auth-hint">Our team will review your request and be in touch shortly.</p>
        </div>
      </div>
    );
  }

  // Paid plan, payment confirmed — auto-approved, ready to sign in.
  if (paid) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>You're approved</h1>
          </div>
          <div className="banner info" style={{ marginBottom: 16 }}>
            <Check />
            <span>
              {data.planName ? "Payment confirmed — your plan is active." : "Payment confirmed — your credits are ready."}
            </span>
          </div>
          <Link to="/login" className="btn primary" style={{ width: "100%", justifyContent: "center" }}>
            Continue to sign in
          </Link>
        </div>
      </div>
    );
  }

  // Paid plan, verified — collect payment now.
  const cur = (data.currency ?? "INR") as BillingCurrency;
  const priced = data.amountMinor != null ? formatMinor(data.amountMinor, cur) : null;
  const isPlan = !!data.planName;
  const qty = data.quantity ?? 1;
  return (
    <div className="auth-wrap t1-auth">
      <AuthBrand />
      <div className="auth-card">
        <div className="auth-head">
          <span className="mark">
            <ShieldCheck />
          </span>
          <h1>Email verified</h1>
          <p>
            {isPlan
              ? "One step left — pay for your plan and your workspace starts right away."
              : `One step left — pay for your ${data.credits?.toLocaleString("en-IN")}-credit pack and you're in.`}
          </p>
        </div>
        {isPlan && (
          <dl className="t1-signup-summary">
            <div>
              <dt>Plan</dt>
              <dd>
                {data.planName}
                {qty > 1 ? ` × ${qty}` : ""}
              </dd>
            </div>
            {data.allowances && (
              <div>
                <dt>Includes</dt>
                <dd>
                  {[
                    allowanceLabel("job", data.allowances.jobs),
                    allowanceLabel("scan", data.allowances.resumes),
                    allowanceLabel("interview", data.allowances.interviews),
                  ].join(" · ")}
                </dd>
              </div>
            )}
            {priced && (
              <div className="total">
                <dt>Total</dt>
                <dd>
                  <b className="tnum">{priced}</b>
                  {data.taxMinor != null && data.taxMinor > 0 && (
                    <small>
                      incl. {formatMinor(data.taxMinor, cur)} GST{data.taxPct ? ` (${data.taxPct}%)` : ""}
                    </small>
                  )}
                </dd>
              </div>
            )}
          </dl>
        )}
        {payError && (
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>{payError}</span>
          </div>
        )}
        <button
          className="btn primary"
          type="button"
          style={{ width: "100%", justifyContent: "center" }}
          disabled={payBusy}
          onClick={() => void pay()}
        >
          {payBusy ? "Opening payment…" : priced ? `Pay ${priced}` : "Pay now"}
        </button>
      </div>
    </div>
  );
}
